import { useEffect, useRef } from 'react';

import {
  isRunningInNativeApp,
  native,
  useNavigateWithTransition,
} from '@lemoment/shared';

import { isTapSource } from './pushSources';
import { toRoutePath } from './useDeepLink';

// `useNavigateWithTransition` is async/animated in the native shell;
// collapse rapid bursts (e.g. two queued Notifee taps drained
// back-to-back) into one navigate() to the LAST resolved path.
const COLLAPSE_WINDOW_MS = 50;

// pushId dedup across the paths that can surface a single tap (FCM
// cold-start, OEM re-delivery, Notifee tap forwarded as pushReceived).
// Module scope survives React re-mounts on purpose.
const recentPushIds = new Map<string, number>();
const DEDUP_WINDOW_MS = 30_000;
const DEDUP_MAX_SIZE = 200;

const remember = (pushId: string): boolean => {
  const now = Date.now();
  const expiresAt = recentPushIds.get(pushId);
  if (expiresAt !== undefined && expiresAt > now) {
    return false;
  }
  if (expiresAt !== undefined) {
    recentPushIds.delete(pushId);
  } else if (recentPushIds.size >= DEDUP_MAX_SIZE) {
    const oldest = recentPushIds.keys().next().value;
    if (oldest !== undefined) {
      recentPushIds.delete(oldest);
    }
  }
  recentPushIds.set(pushId, now + DEDUP_WINDOW_MS);
  return true;
};

// Same-origin in-app path from a (possibly fully-qualified) redirect URL.
// Foreign origins are rejected so a push can't navigate off-app.
const safeInAppPath = (redirectUrl: string): string | null => {
  try {
    const url = new URL(redirectUrl, window.location.origin);
    if (url.origin !== window.location.origin) {
      return null;
    }
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
};

// Resolve a push payload to an in-app route. Prefer `redirectUrl`
// (same-origin path) over `url` (a fully-qualified deep link). Shared by
// tap-navigation and the foreground toast.
export const resolvePushRoute = (
  data: Record<string, string> | undefined,
): string | null => {
  const redirectUrl = data?.redirectUrl;
  if (redirectUrl) {
    const path = safeInAppPath(redirectUrl);
    if (path) {
      return path;
    }
  }
  const rawUrl = data?.url;
  if (rawUrl) {
    return toRoutePath(rawUrl);
  }
  return null;
};

/**
 * Navigate when the user taps a push notification. No-op for foreground
 * messages — those surface via `useForegroundPushToast`. No-op in a plain
 * browser.
 */
export const usePushTap = (): void => {
  const navigate = useNavigateWithTransition();
  const pendingPathRef = useRef<string | null>(null);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isRunningInNativeApp()) {
      return;
    }

    const scheduleNavigate = (path: string) => {
      pendingPathRef.current = path;
      if (collapseTimerRef.current) {
        return;
      }
      collapseTimerRef.current = setTimeout(() => {
        const next = pendingPathRef.current;
        pendingPathRef.current = null;
        collapseTimerRef.current = null;
        if (next) {
          void navigate(next);
        }
      }, COLLAPSE_WINDOW_MS);
    };

    const unsubscribe = native.on('pushReceived', ({ data, source }) => {
      if (!isTapSource(source)) {
        return;
      }
      const pushId = data?.pushId;
      if (pushId && !remember(pushId)) {
        return;
      }
      const path = resolvePushRoute(data);
      if (path) {
        scheduleNavigate(path);
      }
    });

    return () => {
      unsubscribe();
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
      pendingPathRef.current = null;
    };
  }, [navigate]);
};
